import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Grid,
  SelectChangeEvent,
  TextField,
  Typography
} from '@mui/material';
import { SidePanel, SidePanelContent, SidePanelFooter } from '@securspace/securspace-ui-kit';
import SimpleConfirmationDialog from '../../SimpleConfirmationDialog';
import SimpleSelect from '../../SimpleSelect';
import { PendingInventoryActivityVO } from '../../../types/model/valueObject/pendinginventoryactivity/PendingInventoryActivityVO';
import {
  patchPendingInventoryActivity,
  resubmitPendingInventoryActivity,
  PendingInventoryActivityPatchPayload,
} from './requests/pending-inventory-activity-requests';
import { getErrorMessageForStandardResponse } from '../../../util/NetworkErrorUtil';
import { AssetType, AssetTypeLabel, assetSizes } from '../../constants/securspace-constants';
import {useSnackbar} from "../../../context/snackbar-context";

// ─── Constants ──────────────────────────────────────────────────────────────

const PENDING_STATUS = 'PENDING';
const RESOLVED_STATUS = 'RESOLVED';

const ASSET_TYPE_OPTIONS = Object.keys(AssetType)
  .filter((key) => key !== 'ALL')
  .map((key) => ({
    value: AssetType[key as keyof typeof AssetType],
    label: (AssetTypeLabel as Record<string, string>)[AssetType[key as keyof typeof AssetType]] ?? key,
  }));

const ASSET_SIZE_OPTIONS = assetSizes.map((s: string) => ({ value: s, label: `${s}'` }));

// ─── Types ──────────────────────────────────────────────────────────────────
interface EditPendingInventoryActivityPanelProps {
  open: boolean;
  item: PendingInventoryActivityVO | null;
  onClose: () => void;
  /** Called after a successful save (PATCH) or resubmit so the parent can refresh */
  onActionSuccess: () => void;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Extract only the editable fields from a PendingInventoryActivityVO
 */
const toEditableFields = (item: PendingInventoryActivityVO): PendingInventoryActivityPatchPayload => ({
  bookingNumber: item.bookingNumber ?? '',
  containerNumber: item.containerNumber ?? '',
  trailerNumber: item.trailerNumber ?? '',
  chassisNumber: item.chassisNumber ?? '',
  chassisLicensePlateNumber: item.chassisLicensePlateNumber ?? '',
  sealNumber: item.sealNumber ?? '',
  driverFirstName: item.driverFirstName ?? '',
  driverLastName: item.driverLastName ?? '',
  driverLicenseNumber: item.driverLicenseNumber ?? '',
  truckLicensePlateNumber: item.truckLicensePlateNumber ?? '',
  assetType: item.assetType ?? '',
  assetSize: item.assetSize ?? '',
  scacCode: item.scacCode ?? '',
  notes: item.notes ?? '',
});

// ─── Component ──────────────────────────────────────────────────────────────

/**
 * Side panel for editing and resubmitting a pending inventory activity.
 *
 * - **Save** calls PATCH (persists corrections, no validation)
 * - **Resubmit** calls POST /resubmit (re-validates and processes into real inventory activity)
 */
const EditPendingInventoryActivityPanel = ({
  open,
  item,
  onClose,
  onActionSuccess,
}: EditPendingInventoryActivityPanelProps) => {
  // ── Form state ───────────────────────────────────────────────────────────
  const [formData, setFormData] = useState<PendingInventoryActivityPatchPayload>({});
  const [formDirty, setFormDirty] = useState(false);
  const [discardDialog, setDiscardDialog] = useState(false);

  // ── Action state ─────────────────────────────────────────────────────────
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Validation error from resubmit (inline display) ──────────────────────
  const [validationError, setValidationError] = useState<string | null>(null);
  const [validationErrorCode, setValidationErrorCode] = useState<string | null>(null);

  // ── Message Display ──────────────────────
  const { snackbarShowMessage } = useSnackbar();

  // ── Reset form when item changes ─────────────────────────────────────────
  useEffect(() => {
    if (item) {
      setFormData(toEditableFields(item));
      setValidationError(
        item.status === PENDING_STATUS && item.validationErrorMessage
          ? item.validationErrorMessage
          : null,
      );
      setValidationErrorCode(
        item.status === PENDING_STATUS && item.validationErrorCodeLabel
          ? item.validationErrorCodeLabel
          : null,
      );
    }
    setFormDirty(false);
    setError(null);
  }, [item]);

  // ── Close handling with unsaved changes protection ───────────────────────
  const handleCloseAttempt = useCallback((): boolean => {
    if (formDirty) {
      setDiscardDialog(true);
      return false;
    }
    return true;
  }, [formDirty]);

  const handleForceClose = () => {
    setDiscardDialog(false);
    setFormDirty(false);
    setError(null);
    setValidationError(null);
    setValidationErrorCode(null);
    onClose();
  };

  // ── Field change handlers ────────────────────────────────────────────────
  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormDirty(true);
    setError(null);
  };

  const handleSelectChange = (name: string) => (e: SelectChangeEvent) => {
    setFormData((prev) => ({ ...prev, [name]: e.target.value }));
    setFormDirty(true);
    setError(null);
  };

  // ── Resubmit: save (PATCH) then resubmit (POST) ─────────────────────────
  const handleResubmit = async () => {
    if (!item) return;

    setSubmitting(true);
    setError(null);
    setValidationError(null);
    setValidationErrorCode(null);

    try {
      // Step 1 — persist edits
      await patchPendingInventoryActivity(item.id, formData);
      setFormDirty(false);

      // Step 2 — resubmit for validation
      const { body: updatedItem } = await resubmitPendingInventoryActivity(item.id);

      if (updatedItem.status === RESOLVED_STATUS) {
        snackbarShowMessage('Activity resubmitted successfully and resolved.', 'success');
        onActionSuccess();
        handleForceClose();
      } else {
        // Validation failed — refresh the form with the latest data
        setFormData(toEditableFields(updatedItem));
        setValidationError(
          updatedItem.validationErrorMessage || 'Resubmit failed: validation error.',
        );
        setValidationErrorCode(updatedItem.validationErrorCodeLabel ?? null);
        onActionSuccess();
      }
    } catch (err: unknown) {
      const msg = getErrorMessageForStandardResponse(err, 'Failed to resubmit. Please try again.');
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };
  const isPending = item?.status === PENDING_STATUS;

  return (
    <>
      <SidePanel
        open={open}
        onClose={onClose}
        onCloseAttempt={handleCloseAttempt}
        title="Edit Pending Activity"
      >
        <SidePanelContent>
          {!item ? (
            <Grid container justifyContent="center" p="2rem">
              <CircularProgress />
            </Grid>
          ) : (
            <Grid container direction="column" gap={2}>
              {/* Validation error from resubmit */}
              {validationError && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    Validation Error
                  </Typography>
                  {validationErrorCode && (
                    <Typography variant="caption" color="text.secondary">
                      {validationErrorCode}
                    </Typography>
                  )}
                  <Typography variant="body2">{validationError}</Typography>
                </Alert>
              )}

              {/* General error */}
              {error && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  {error}
                </Alert>
              )}

              {/* Read-only fields */}
              <Typography variant="overline" color="text.secondary">
                Event Information (read-only)
              </Typography>
              <TextField
                label="Event Type"
                value={item.eventTypeLabel ?? ''}
                InputProps={{ readOnly: true }}
                variant="standard"
                fullWidth
                disabled
              />
              <TextField
                label="Event Date/Time"
                value={item.eventDatetime ?? ''}
                InputProps={{ readOnly: true }}
                variant="standard"
                fullWidth
                disabled
              />
              <TextField
                label="Event User"
                value={item.eventUser ?? ''}
                InputProps={{ readOnly: true }}
                variant="standard"
                fullWidth
                disabled
              />
              <TextField
                label="Status"
                value={item.statusLabel ?? ''}
                InputProps={{ readOnly: true }}
                variant="standard"
                fullWidth
                disabled
              />

              {/* Editable fields */}
              <Typography variant="overline" color="text.secondary" sx={{ mt: 1 }}>
                Editable Fields
              </Typography>
              <TextField
                label="Booking Number"
                name="bookingNumber"
                value={formData.bookingNumber ?? ''}
                onChange={handleTextChange}
                variant="standard"
                fullWidth
                disabled={submitting}
              />
              <TextField
                label="Container Number"
                name="containerNumber"
                value={formData.containerNumber ?? ''}
                onChange={handleTextChange}
                variant="standard"
                fullWidth
                disabled={submitting}
              />
              <TextField
                label="Trailer Number"
                name="trailerNumber"
                value={formData.trailerNumber ?? ''}
                onChange={handleTextChange}
                variant="standard"
                fullWidth
                disabled={submitting}
              />
              <TextField
                label="Chassis Number"
                name="chassisNumber"
                value={formData.chassisNumber ?? ''}
                onChange={handleTextChange}
                variant="standard"
                fullWidth
                disabled={submitting}
              />
              <TextField
                label="Chassis License Plate"
                name="chassisLicensePlateNumber"
                value={formData.chassisLicensePlateNumber ?? ''}
                onChange={handleTextChange}
                variant="standard"
                fullWidth
                disabled={submitting}
              />
              <TextField
                label="Seal Number"
                name="sealNumber"
                value={formData.sealNumber ?? ''}
                onChange={handleTextChange}
                variant="standard"
                fullWidth
                disabled={submitting}
              />
              <TextField
                label="Driver First Name"
                name="driverFirstName"
                value={formData.driverFirstName ?? ''}
                onChange={handleTextChange}
                variant="standard"
                fullWidth
                disabled={submitting}
              />
              <TextField
                label="Driver Last Name"
                name="driverLastName"
                value={formData.driverLastName ?? ''}
                onChange={handleTextChange}
                variant="standard"
                fullWidth
                disabled={submitting}
              />
              <TextField
                label="Driver License Number"
                name="driverLicenseNumber"
                value={formData.driverLicenseNumber ?? ''}
                onChange={handleTextChange}
                variant="standard"
                fullWidth
                disabled={submitting}
              />
              <TextField
                label="Truck License Plate"
                name="truckLicensePlateNumber"
                value={formData.truckLicensePlateNumber ?? ''}
                onChange={handleTextChange}
                variant="standard"
                fullWidth
                disabled={submitting}
              />
              <SimpleSelect
                id="assetType"
                label="Asset Type"
                name="assetType"
                value={formData.assetType ?? ''}
                onChange={handleSelectChange('assetType')}
                options={ASSET_TYPE_OPTIONS}
                displayEmpty
                placeholder="Select asset type"
              />
              <SimpleSelect
                id="assetSize"
                label="Asset Size"
                name="assetSize"
                value={formData.assetSize ?? ''}
                onChange={handleSelectChange('assetSize')}
                options={ASSET_SIZE_OPTIONS}
                displayEmpty
                placeholder="Select asset size"
              />
              <TextField
                label="SCAC Code"
                name="scacCode"
                value={formData.scacCode ?? ''}
                onChange={handleTextChange}
                variant="standard"
                fullWidth
                disabled={submitting}
              />
              <TextField
                label="Notes"
                name="notes"
                value={formData.notes ?? ''}
                onChange={handleTextChange}
                variant="standard"
                fullWidth
                multiline
                minRows={2}
                disabled={submitting}
              />
            </Grid>
          )}
        </SidePanelContent>

        <SidePanelFooter>
          {isPending && (
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleResubmit}
              disabled={submitting}
              sx={{ textTransform: 'none', fontSize: '1rem', py: 1.5 }}
            >
              {submitting ? <CircularProgress size={24} /> : 'Resubmit'}
            </Button>
          )}
        </SidePanelFooter>
      </SidePanel>

      {/* Discard unsaved changes confirmation */}
      <SimpleConfirmationDialog
        open={discardDialog}
        onClose={() => setDiscardDialog(false)}
        onConfirm={handleForceClose}
        title="Discard Changes?"
        body="You have unsaved changes. Are you sure you want to close without saving?"
      />
    </>
  );
};

export { EditPendingInventoryActivityPanel };
